// Inline autocomplete for adding project, context, or person tags in TaskFormTagSection.
import { useEffect, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { autocompleteTextFieldSx } from '@/features/tasks/components/forms/taskFormStyles';

/** @typedef {'project'|'context'|'person'} InlineAddKind */

/**
 * @param {{
 *   inlineAddKind: InlineAddKind|null,
 *   inlineDraft: string,
 *   setInlineDraft: (value: string) => void,
 *   inlineOptions: string[],
 *   inlinePlaceholder: string,
 *   commitInlineAdd: (rawValue?: string) => void,
 *   cancelInlineAdd: () => void,
 * }} props
 */
export const TaskFormTagInlineAddField = ({
  inlineAddKind,
  inlineDraft,
  setInlineDraft,
  inlineOptions,
  inlinePlaceholder,
  commitInlineAdd,
  cancelInlineAdd,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!inlineAddKind) return;
    // MUI Menu restores focus to its trigger on close, racing autoFocus — force focus after that settles.
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [inlineAddKind]);

  if (!inlineAddKind) return null;

  return (
    <Autocomplete
      freeSolo
      autoFocus
      size="small"
      options={inlineOptions}
      inputValue={inlineDraft}
      onInputChange={(_, value) => setInlineDraft(value)}
      onChange={(_, value) => {
        if (value) commitInlineAdd(value);
      }}
      disableClearable
      sx={{ width: 180, maxWidth: '100%' }}
      slotProps={{
        popper: { disablePortal: true },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="outlined"
          placeholder={inlinePlaceholder}
          sx={autocompleteTextFieldSx}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitInlineAdd(inlineDraft);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              cancelInlineAdd();
            }
          }}
          onBlur={() => {
            if (!inlineDraft.trim()) cancelInlineAdd();
          }}
        />
      )}
    />
  );
};
